import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton, Switch, Typography, useTheme } from '@mui/material';

import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { Table, usePagination } from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { formatISOStringToFriendlyDate } from '@frontend/features/common/helper';
import { useGetAllFlightIntegrationsByAgencyId } from '@frontend/features/agency-flight-integrations/queries';
import { useParams } from 'react-router-dom';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useQueryClient } from 'react-query';
import { ConfirmDisableAgencyFlightIntegrationModal } from '@frontend/features/agency-flight-integrations/components/ConfirmDisableAgencyFlightIntegrationModal';

export const AgencyFlightIntegrationsList = () => {
  const { t } = useTranslation();
  const { id: agencyId } = useParams();
  const pagination = usePagination();
  const { addToast } = useSnackBar();
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [integrationIdToDisable, setIntegrationIdToDisable] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [isTogglingIntegration, setIsTogglingIntegration] = useState(false);
  const { data, isLoading } = useGetAllFlightIntegrationsByAgencyId({
    agencyId: Number(agencyId),
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
  });

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  const handleEnable = async (flightIntegrationId: string) => {
    try {
      setIsTogglingIntegration(true);
      await client.post('/agency-flight-integrations', {
        flightIntegrationId: flightIntegrationId,
        agencyId: Number(agencyId),
      });
      queryClient.invalidateQueries({
        queryKey: QueryKeys.getAllAgencyFlightIntegrations,
      });
      addToast({
        message: t('agencyFlightIntegrations.enabled.success'),
        color: 'success',
      });
    } catch (error) {
      addToast({
        message: t('agencyFlightIntegrations.enabled.error'),
        color: 'error',
      });
      console.error(error);
    } finally {
      setIsTogglingIntegration(false);
    }
  };

  const handleDisable = async (flightIntegrationId: string) => {
    try {
      setIsTogglingIntegration(true);
      await client.request({
        method: 'DELETE',
        url: '/agency-flight-integrations',
        data: {
          flightIntegrationId: flightIntegrationId,
          agencyId: Number(agencyId),
        },
      });
      queryClient.invalidateQueries({
        queryKey: QueryKeys.getAllAgencyFlightIntegrations,
      });
      addToast({
        message: t('agencyFlightIntegrations.disabled.success'),
        color: 'success',
      });
    } catch (error) {
      addToast({
        message: t('agencyFlightIntegrations.disabled.error'),
        color: 'error',
      });
      console.error(error);
    } finally {
      setIsTogglingIntegration(false);
    }
  };

  const toggleConfirmationPopup = async (flightIntegrationId: string, changeToEnable: boolean) => {
    if (changeToEnable) {
      handleEnable(flightIntegrationId);
    } else {
      setIntegrationIdToDisable(flightIntegrationId);
    }
  };

  const handleCloseDisableConfirmationModal = (confirmDisable: boolean) => {
    if (confirmDisable && integrationIdToDisable) {
      handleDisable(integrationIdToDisable);
    }
    setIntegrationIdToDisable(null);
  };

  return (
    <ListLayout title={t('agencyFlightIntegrations.title')} subtitle={t('agencyFlightIntegrations.subtitle')}>
      <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
        <Typography
          variant="subtitle1"
          align="left"
          color={theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          {data?.count || 0} {t('agencyFlightIntegrations.results')}
        </Typography>

        <SearchInput placeholder={t('agencyFlightIntegrations.search')} onSearch={handleOnSearch} />
      </Box>

      {Boolean(data?.count) && (
        <Table
          columns={[
            {
              id: 'provider',
              label: t('agencyFlightIntegrations.table.provider'),
              align: 'left',
            },
            {
              id: 'ownerClientName',
              label: t('agencyFlightIntegrations.table.ownerClientName'),
              align: 'left',
            },
            {
              id: 'isShared',
              label: t('agencyFlightIntegrations.table.isShared'),
              align: 'left',
              format: (value) => <>{value ? t('common.yes') : t('common.no')}</>,
            },
            {
              id: 'createdAt',
              label: t('agencyFlightIntegrations.table.createdAt'),
              align: 'left',
            },
            {
              id: 'isEnabledForAgency',
              label: t('agencyFlightIntegrations.table.isEnabledForAgency'),
              align: 'left'
            },
          ]}
          data={{
            key: 'id',
            items: (data?.items || []).map((item) => ({
              provider: item.provider,
              ownerClientName: item.ownerClientName,
              isShared: item.isShared,
              createdAt: formatISOStringToFriendlyDate(item.createdAt),
              isEnabledForAgency: (
                <Switch
                  title={
                    item.isEnabledForAgency
                      ? t('agencyFlightIntegrations.table.itemEnabled')
                      : t('agencyFlightIntegrations.table.itemDisabled')
                  }
                  disabled={isLoading || isTogglingIntegration}
                  checked={item.isEnabledForAgency}
                  onChange={(e) => toggleConfirmationPopup(item.id, e.target.checked)}
                />
              ),
            })),
            totalCount: data?.count || 0,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}
      {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
      <ConfirmDisableAgencyFlightIntegrationModal
        onClose={handleCloseDisableConfirmationModal}
        open={integrationIdToDisable !== null}
      />
    </ListLayout>
  );
};
